import type maplibregl from "maplibre-gl";

import type { DeforestationLayerStates } from "../types/deforestation";

function setLayerVisibility(
  m: maplibregl.Map,
  layerId: string,
  visible: boolean,
) {
  if (m.getLayer(layerId)) {
    m.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
  }
}

function applyContextPmtiles(
  m: maplibregl.Map,
  fillLayerId: string,
  lineLayerId: string,
  state: { visible: boolean; opacity: number; color: string },
  fillOpacityMultiplier = 0.3,
  options: { preserveColor?: boolean } = {},
) {
  if (m.getLayer(fillLayerId)) {
    setLayerVisibility(m, fillLayerId, state.visible);
    // Colour only matters while the layer is drawn — skip the write when
    // hidden (opacity is 0 anyway) and re-apply it when it turns visible.
    // `preserveColor` keeps a categorical paint expression intact (e.g.
    // the regulated-veg layer colours features by rvm_cat).
    if (state.visible && !options.preserveColor) {
      m.setPaintProperty(fillLayerId, "fill-color", state.color);
    }
    m.setPaintProperty(
      fillLayerId,
      "fill-opacity",
      state.visible ? state.opacity * fillOpacityMultiplier : 0,
    );
  }
  if (m.getLayer(lineLayerId)) {
    setLayerVisibility(m, lineLayerId, state.visible);
    if (state.visible && !options.preserveColor) {
      m.setPaintProperty(lineLayerId, "line-color", state.color);
    }
    m.setPaintProperty(
      lineLayerId,
      "line-opacity",
      state.visible ? state.opacity : 0,
    );
  }
}

function applyImageryRaster(
  m: maplibregl.Map,
  layerId: string,
  state: { visible: boolean; opacity: number },
) {
  if (!m.getLayer(layerId)) return;
  setLayerVisibility(m, layerId, state.visible);
  m.setPaintProperty(
    layerId,
    "raster-opacity",
    state.visible ? state.opacity : 0,
  );
}

export function applyLayerState(
  m: maplibregl.Map,
  target: DeforestationLayerStates,
) {
  applyImageryRaster(m, "imagery-2020-raster", target.imagery2020);
  applyImageryRaster(m, "imagery-2025-raster", target.imagery2025);
  applyImageryRaster(m, "imagery-latest-raster", target.imageryLatest);
  applyImageryRaster(m, "forest-2020-raster", target.forest2020);
  applyImageryRaster(m, "forest-2025-raster", target.forest2025);
  applyImageryRaster(m, "crown-cover-2020-raster", target.crownCover2020);
  applyImageryRaster(m, "crown-cover-2025-raster", target.crownCover2025);
  applyImageryRaster(m, "forest-change-raster", target.forestChange);
  applyImageryRaster(m, "lulc-2025-raster", target.lulc2025);

  if (m.getLayer("geoscape-cadastre-fill")) {
    setLayerVisibility(m, "geoscape-cadastre-fill", target.cadastre.visible);
    setLayerVisibility(m, "geoscape-cadastre-line", target.cadastre.visible);
    setLayerVisibility(m, "geoscape-cadastre-labels", target.cadastre.visible);
    m.setPaintProperty(
      "geoscape-cadastre-fill",
      "fill-color",
      target.cadastre.color,
    );
    m.setPaintProperty(
      "geoscape-cadastre-fill",
      "fill-opacity",
      target.cadastre.visible ? 0.1 * target.cadastre.opacity : 0,
    );
    m.setPaintProperty(
      "geoscape-cadastre-line",
      "line-color",
      target.cadastre.color,
    );
    m.setPaintProperty(
      "geoscape-cadastre-line",
      "line-opacity",
      target.cadastre.visible ? target.cadastre.opacity : 0,
    );
  }

  if (m.getLayer("geoscape-property-fill")) {
    setLayerVisibility(m, "geoscape-property-fill", target.property.visible);
    setLayerVisibility(m, "geoscape-property-line", target.property.visible);
    if (target.property.visible) {
      m.setPaintProperty(
        "geoscape-property-fill",
        "fill-color",
        target.property.color,
      );
      m.setPaintProperty(
        "geoscape-property-line",
        "line-color",
        target.property.color,
      );
    }
    m.setPaintProperty(
      "geoscape-property-fill",
      "fill-opacity",
      target.property.visible ? 0.1 * target.property.opacity : 0,
    );
    m.setPaintProperty(
      "geoscape-property-line",
      "line-opacity",
      target.property.visible ? target.property.opacity : 0,
    );
  }

  if (m.getLayer("nab-portfolio-line")) {
    setLayerVisibility(m, "nab-portfolio-line", target.nabPortfolio.visible);
    m.setPaintProperty(
      "nab-portfolio-line",
      "line-color",
      target.nabPortfolio.color,
    );
    m.setPaintProperty(
      "nab-portfolio-line",
      "line-opacity",
      target.nabPortfolio.visible ? target.nabPortfolio.opacity : 0,
    );
  }

  applyContextPmtiles(
    m,
    "context-capad-fill",
    "context-capad-line",
    target.protectedMatters,
  );
  applyContextPmtiles(
    m,
    "context-ramsar-fill",
    "context-ramsar-line",
    target.ramsarWetlands,
  );
  applyContextPmtiles(
    m,
    "context-world-heritage-fill",
    "context-world-heritage-line",
    target.worldHeritage,
  );
  applyContextPmtiles(
    m,
    "context-national-heritage-fill",
    "context-national-heritage-line",
    target.nationalHeritage,
  );
  applyContextPmtiles(
    m,
    "context-commonwealth-heritage-fill",
    "context-commonwealth-heritage-line",
    target.commonwealthHeritage,
  );
  applyContextPmtiles(
    m,
    "context-rfa-fill",
    "context-rfa-line",
    target.regionalForestAgreements,
  );
  applyContextPmtiles(
    m,
    "context-regulated-veg-fill",
    "context-regulated-veg-line",
    target.regulatedVeg,
    0.3,
    { preserveColor: true },
  );
  applyContextPmtiles(
    m,
    "context-bushfire-history-fill",
    "context-bushfire-history-line",
    target.bushfireHistory,
  );
  applyContextPmtiles(
    m,
    "context-koala-habitat-fill",
    "context-koala-habitat-line",
    target.koalaHabitat,
  );
  applyContextPmtiles(
    m,
    "context-critical-habitat-fill",
    "context-critical-habitat-line",
    target.listedCriticalHabitat,
  );

  // NVIS is a paletted PNG raster — its colours are baked into the PNG,
  // so we ignore the colour swatch and just toggle visibility/opacity.
  applyImageryRaster(m, "context-nvis-mvs-raster", target.nvisVegetation);
}

export { setLayerVisibility };
